import type { Background } from '@/api/types/background'
import type { SavedSpreadBackgrounds } from '@/lib/backgrounds/saved-spread-backgrounds'

/**
 * Session-scoped "Placed" state for the Backgrounds tray — the backgrounds twin of
 * `lib/photos/photo-placement-session`. The library response's `placedFlag` comes from a
 * book-wide backend scan that only reruns after logout, so a background placed (or removed) this
 * session would otherwise keep its stale tag until the next login.
 *
 * Tracked per spread: backgroundId -> spreadIds where it currently fills a slot (this session's
 * view). Per spread so that switching spreads doesn't lose a placement made on another one.
 */
const placementsBySpread = new Map<string, Set<string>>()

/**
 * Backgrounds this session has seen on a spread at least once. Kept for the whole session, so a
 * background placed and then removed again is forced back to NOT placed instead of falling back to
 * the server's stale `placedFlag`. A background this session never saw on a spread stays absent
 * from the snapshot, so its tag keeps trusting the server.
 */
const sessionTrackedBackgroundIds = new Set<string>()

const listeners = new Set<() => void>()

/** Immutable snapshot for `useSyncExternalStore` — a new reference whenever the overrides change.
 *  true = on at least one spread this session; false = seen this session but no longer on any
 *  tracked spread; absent = no opinion (use the server's value). */
let placementOverridesSnapshot: ReadonlyMap<string, boolean> = new Map()

function emitPlacementChange() {
  const flat = new Map<string, boolean>()

  for (const backgroundId of sessionTrackedBackgroundIds) {
    const spreadIds = placementsBySpread.get(backgroundId)

    flat.set(backgroundId, !!spreadIds && spreadIds.size > 0)
  }

  placementOverridesSnapshot = flat
  for (const listener of listeners) listener()
}

export function subscribeBackgroundPlacementOverrides(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getBackgroundPlacementOverridesSnapshot(): ReadonlyMap<string, boolean> {
  return placementOverridesSnapshot
}

/** The background ids filling any of a spread's slots. */
export function placedBackgroundIds(saved: SavedSpreadBackgrounds): Set<string> {
  const ids = new Set<string>()

  for (const ref of [saved.double, saved.left, saved.right]) {
    if (ref) ids.add(ref.id)
  }

  return ids
}

/**
 * Aligns `spreadId`'s slice of the session state with the backgrounds now on it — called whenever
 * that spread's saved backgrounds change (place, replace, remove, undo/redo, rollback, hydration).
 * Every other spread's slice is left alone. Notifies subscribers only when something changed.
 */
export function reconcileBackgroundPlacementOverrides(
  spreadId: string,
  onSpread: ReadonlySet<string>
): void {
  let changed = false

  for (const backgroundId of onSpread) {
    if (!sessionTrackedBackgroundIds.has(backgroundId)) {
      sessionTrackedBackgroundIds.add(backgroundId)
      changed = true
    }

    const spreadIds = placementsBySpread.get(backgroundId)

    if (!spreadIds) {
      placementsBySpread.set(backgroundId, new Set([spreadId]))
      changed = true
    } else if (!spreadIds.has(spreadId)) {
      spreadIds.add(spreadId)
      changed = true
    }
  }

  for (const [backgroundId, spreadIds] of placementsBySpread) {
    if (spreadIds.has(spreadId) && !onSpread.has(backgroundId)) {
      spreadIds.delete(spreadId)
      if (spreadIds.size === 0) placementsBySpread.delete(backgroundId)
      changed = true
    }
  }

  if (changed) emitPlacementChange()
}

/** Logout only — a fresh login gets fresh server-computed `placedFlag` data, so nothing
 *  session-local carries over. Not called on spread switch (see the module doc). */
export function clearBackgroundPlacementOverrides(): void {
  if (placementsBySpread.size === 0 && sessionTrackedBackgroundIds.size === 0) return
  placementsBySpread.clear()
  sessionTrackedBackgroundIds.clear()
  emitPlacementChange()
}

/** Lays the session overrides over the library response's `placedFlag`. Unchanged backgrounds
 *  keep their identity, and with no overrides the input array itself is returned. */
export function applyBackgroundPlacementOverrides(
  backgrounds: Background[],
  overrides: ReadonlyMap<string, boolean> = placementOverridesSnapshot
): Background[] {
  if (overrides.size === 0) return backgrounds

  return backgrounds.map((background) => {
    const placedFlag = overrides.get(background.backgroundId)

    if (placedFlag === undefined || background.placedFlag === placedFlag) return background

    return { ...background, placedFlag }
  })
}
